import { BarChart3Icon, CheckIcon, CopyIcon, FileTextIcon, HistoryIcon, KeyRoundIcon, LayoutGridIcon, MapPinIcon, PhoneIcon, SendIcon, SettingsIcon, UploadIcon, UsersIcon, UsersRoundIcon, ZapIcon } from 'lucide-react';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router';
import { ErrorState } from '@/components/ErrorState';
import { JobMark } from '@/components/JobMark';
import { TabStrip, type TabItem, type TabLink } from '@/components/TabStrip';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useJob } from '@/features/jobs/queries';
import { useMe } from '@/lib/auth';
import { tzAbbreviation } from '@/lib/format';
import { jobAccent } from '@/lib/jobStyle';
import type { JobDetailResponse, JobDto, JobPermissions } from '@/lib/types';
import { cn } from '@/lib/utils';

const JobContext = createContext<JobDetailResponse | null>(null);

export function useJobContext(): JobDetailResponse {
  const ctx = useContext(JobContext);
  if (!ctx) throw new Error('useJobContext must be used within JobLayout');
  return ctx;
}

/**
 * Job frame: a masthead. The title area and the tabs share one surface that sits flush under the top bar, with a
 * rule in the job's own colour across the top. Name, two quiet pills (status, your level), the two facts that
 * identify a site (its number, its city and time zone), the actions on the right, and beneath them the five
 * everyday sections as tabs with icons and counts; configuration under "More", which reads as the section's name
 * while you are in it. The active tab is underlined in the job's colour, so the whole block belongs to this job.
 */
export function JobLayout() {
  const { jobId = '' } = useParams();
  const { pathname } = useLocation();
  const query = useJob(jobId);
  const me = useMe();
  const f = me.tenant.features;

  if (query.isPending) {
    return (
      <div className="space-y-6">
        <div className="bg-card border px-4 pt-5 pb-0 md:px-6 md:pt-6">
          <div className="flex items-center gap-4 pb-5">
            <Skeleton className="size-12" />
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-3 w-72" />
            </div>
          </div>
          <Skeleton className="mb-3 h-6 w-96" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} title="Could not load this job" />;

  const { job, permissions } = query.data;
  const base = `/jobs/${job.id}`;
  const accent = jobAccent(job);
  const primary: TabLink[] = [
    { to: `${base}/overview`, label: 'Overview', icon: LayoutGridIcon },
    { to: `${base}/contacts`, label: 'Contacts', icon: UsersIcon, badge: job.contactCount ?? undefined },
    // PM Users cannot send texts, so Compose is not offered.
    ...(permissions.send ? [{ to: `${base}/compose`, label: 'Compose', icon: SendIcon }] : []),
    { to: `${base}/history`, label: 'History', icon: HistoryIcon },
    { to: `${base}/team`, label: 'Team', icon: UsersRoundIcon, badge: job.memberCount ?? undefined },
  ];
  // Keywords / Automation appear only when the organization has those features on; Reports are for job admins.
  const more: TabLink[] = [
    ...(f.templates ? [{ to: `${base}/templates`, label: 'Templates', icon: FileTextIcon }] : []),
    ...(f.keywords ? [{ to: `${base}/keywords`, label: 'Keywords', icon: KeyRoundIcon }] : []),
    ...(f.weatherAutomation || f.scheduledSends ? [{ to: `${base}/automation`, label: 'Automation', icon: ZapIcon }] : []),
    ...(f.reports && job.myRole === 'admin' ? [{ to: `${base}/reports`, label: 'Reports', icon: BarChart3Icon }] : []),
    { to: `${base}/settings`, label: 'Settings', icon: SettingsIcon },
  ];
  const tabs: TabItem[] = [...primary, { menu: true, label: 'More', items: more }];

  return (
    <JobContext.Provider value={query.data}>
      <div>
        {/* The masthead and the tab strip share the content column and read as one card; the strip sticks on its own. */}
        <Masthead job={job} permissions={permissions} onCompose={pathname.startsWith(`${base}/compose`)} />
        <TabStrip ariaLabel="Job sections" sticky bleed={false} accent={accent} className="bg-card border-x px-2 md:px-3" items={tabs} />
        <div className="pt-6 md:pt-8">
          <Outlet />
        </div>
      </div>
    </JobContext.Provider>
  );
}

// ---------------------------------------------------------------- masthead

function Masthead({ job, permissions, onCompose }: { job: JobDto; permissions: JobPermissions; onCompose: boolean }) {
  const accent = jobAccent(job);
  const active = job.status === 'active';
  const role = job.myRole === 'admin' ? 'PM Admin' : job.myRole === 'user' ? 'PM User' : null;
  const site = shortAddress(job.address);
  const zone = job.timezone ? tzAbbreviation(job.timezone) : null;

  return (
    <header className="bg-card border border-b-0 px-4 md:px-6">
      <div className="-mx-4 h-0.5 md:-mx-6" style={{ backgroundColor: accent }} aria-hidden />
      <div className="flex items-start justify-between gap-6 pt-4 pb-4 md:pt-5 md:pb-5">
        <div className="flex min-w-0 items-start gap-3.5 md:gap-4">
          <JobMark job={job} size="lg" className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <h1 className="font-display min-w-0 truncate text-[2rem] leading-none tracking-tight md:text-[2.5rem]">{job.name}</h1>
              <div className="flex items-center gap-1.5">
                <Pill>
                  <span className={cn('size-1.5 rounded-full', active ? 'bg-success' : 'bg-muted-foreground/40')} aria-hidden />
                  {active ? 'Active' : 'Archived'}
                </Pill>
                {role && <Pill>{role}</Pill>}
              </div>
            </div>
            <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
              {job.phoneNumber ? (
                <CopyNumber display={job.phoneNumber.display} e164={job.phoneNumber.e164} />
              ) : (
                <span className="text-destructive flex items-center gap-1.5">
                  <PhoneIcon className="size-3.5" aria-hidden /> No phone number yet
                </span>
              )}
              {(site || zone) && (
                <span className="flex min-w-0 items-center gap-1.5">
                  <MapPinIcon className="size-3.5 shrink-0" aria-hidden />
                  <span className="truncate">
                    {site ?? 'Site'}
                    {zone && <span className="tabular-nums"> · {zone}</span>}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>

        {(permissions.send || permissions.manageContacts) && active && (
          <div className="hidden shrink-0 items-center gap-2 pt-1 md:flex">
            {permissions.manageContacts && (
              <Button asChild variant="outline">
                <Link to={`/jobs/${job.id}/contacts?import=1`}>
                  <UploadIcon /> Import contacts
                </Link>
              </Button>
            )}
            {permissions.send && !onCompose && (
              <Button asChild>
                <Link to={`/jobs/${job.id}/compose`}>
                  <SendIcon /> Compose
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

function Pill({ children }: { children: ReactNode }) {
  return <span className="text-muted-foreground inline-flex h-6 items-center gap-1.5 border px-2 text-[11px] font-medium whitespace-nowrap">{children}</span>;
}

/** The sending number; click to copy it. */
function CopyNumber({ display, e164 }: { display: string; e164: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="group/copy hover:text-foreground flex items-center gap-1.5 tabular-nums transition-colors"
      title="Copy the number"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(e164);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* ignore */
        }
      }}
    >
      <PhoneIcon className="size-3.5" aria-hidden />
      <span className="text-foreground font-medium">{display}</span>
      {copied ? (
        <span className="text-success flex items-center gap-0.5 text-xs">
          <CheckIcon className="size-3" aria-hidden /> Copied
        </span>
      ) : (
        <CopyIcon className="size-3 opacity-0 transition-opacity group-hover/copy:opacity-60" aria-hidden />
      )}
    </button>
  );
}

/** "400 W Church St, Orlando, FL 32801" → "Orlando, FL". A one-part address is returned as typed. */
function shortAddress(address: string | null | undefined): string | null {
  if (!address?.trim()) return null;
  const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return parts[0] ?? null;
  const city = parts[parts.length - 2] ?? parts[0];
  const region = (parts[parts.length - 1] ?? '').replace(/\s*\d{4,}(-\d{4})?\s*$/, '').trim();
  return region ? `${city}, ${region}` : (city ?? null);
}
